import { createFileRoute, Link } from "@tanstack/react-router";
import { socialMeta } from "@/lib/social-meta";
import { ORG_WEBSITE_JSONLD } from "@/lib/structured-data";
import { MarketingShell, MarketingHero, FeatureGrid, CtaBand } from "@/components/MarketingShell";
import { PageContainer } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  Calendar, Radio, MapPin, FileText, Shield, Building, Users, Clock,
  Bell, MessageSquare, Check, Minus, X, LayoutDashboard, QrCode, Smartphone,
} from "lucide-react";

type Cell = true | false | "partial";
const COMPARE_ROWS: { feature: string; mr: Cell; therms: Cell; tracktik: Cell; silvertrac: Cell }[] = [
  { feature: "All-in-one scheduling + dispatch", mr: true, therms: "partial", tracktik: true, silvertrac: "partial" },
  { feature: "Live GPS officer tracking", mr: true, therms: true, tracktik: true, silvertrac: true },
  { feature: "QR checkpoint + tour verification", mr: true, therms: "partial", tracktik: true, silvertrac: true },
  { feature: "Mobile-native guard app", mr: true, therms: "partial", tracktik: true, silvertrac: "partial" },
  { feature: "Real-time incident reporting", mr: true, therms: true, tracktik: true, silvertrac: true },
  { feature: "Custom report templates", mr: true, therms: true, tracktik: "partial", silvertrac: "partial" },
  { feature: "Client-ready branded PDFs", mr: true, therms: "partial", tracktik: true, silvertrac: "partial" },
  { feature: "Client portal + live coverage feed", mr: true, therms: false, tracktik: true, silvertrac: "partial" },
  { feature: "Transparent pay-as-you-grow pricing", mr: true, therms: true, tracktik: false, silvertrac: "partial" },
  { feature: "Free 30-day trial", mr: true, therms: "partial", tracktik: false, silvertrac: false },
];

function CompareIcon({ value }: { value: Cell }) {
  if (value === true) return <Check className="mx-auto h-5 w-5 text-primary" aria-label="Yes" />;
  if (value === false) return <X className="mx-auto h-5 w-5 text-muted-foreground/60" aria-label="No" />;
  return <Minus className="mx-auto h-5 w-5 text-amber-500" aria-label="Partial" />;
}

export const Route = createFileRoute("/security-guard-management-software")({
  component: SecurityGuardManagementPage,
  head: () => ({
    meta: [
      { title: "Security Guard Management Software — MissionReady" },
      { name: "description", content: "All-in-one security guard management software for scheduling, dispatch, GPS tracking, incident reports, and client-ready proof of service. Start free." },
      ...socialMeta({
        title: "Security Guard Management Software — MissionReady",
        description: "Centralize scheduling, dispatch, GPS tracking, incidents, and client reporting for your security operation in one platform.",
        url: "https://missionready.dev/security-guard-management-software",
      }),
    ],
    links: [{ rel: "canonical", href: "https://missionready.dev/security-guard-management-software" }],
    scripts: [
      ...ORG_WEBSITE_JSONLD,{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "MissionReady Security Guard Management Software",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web, iOS, Android",
        description: "All-in-one security operations platform that centralizes scheduling, dispatch, GPS tracking, incident reporting, site management, patrols, and client reporting for private security teams.",
        url: "https://missionready.dev/security-guard-management-software",
        brand: { "@type": "Brand", name: "MissionReady" },
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD", url: "https://missionready.dev/pricing" },
        featureList: [
          "Scheduling and shift management",
          "Real-time dispatch and communication",
          "GPS officer tracking and QR checkpoints",
          "Mobile incident reporting with photo evidence",
          "Patrol tour verification and replay",
          "Site and client management",
          "Custom report templates and branded PDF exports",
          "Client portal with live coverage feed",
        ],
      }),
    }, {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ_ITEMS.map((it) => ({
          "@type": "Question",
          name: it.q,
          acceptedAnswer: { "@type": "Answer", text: it.a },
        })),
      }),
    }],
  }),
});

const FEATURES = [
  { icon: "📅", title: "Scheduling & shifts", desc: "Build recurring schedules, fill open shifts, and notify officers instantly. Drag-and-drop calendar with conflict detection and coverage alerts." },
  { icon: "📡", title: "Dispatch & real-time comms", desc: "Push incidents, tasks, and updates to the right officer by location or skill. Two-way messaging keeps the field and the office synced." },
  { icon: "📍", title: "GPS tracking & accountability", desc: "See where officers are, where they have been, and when they arrived. Location history proves coverage for clients and disputes." },
  { icon: "📝", title: "Incident reporting", desc: "Mobile-first incident capture with structured forms, photos, videos, and auto-tagged GPS/timestamp evidence." },
  { icon: "🔳", title: "QR checkpoints & tours", desc: "Officers scan QR tags at checkpoints to prove patrol completion. Tours replay on a map with timestamps and missed-stop alerts." },
  { icon: "🏢", title: "Site & client management", desc: "Organize sites, post orders, client contacts, and coverage requirements. Each client sees only what you allow in their portal." },
  { icon: "📊", title: "Custom reports", desc: "No-code report templates turn shift, patrol, and incident data into branded client PDFs and executive dashboards." },
  { icon: "📱", title: "Mobile guard app", desc: "Officers clock in, receive assignments, file reports, scan checkpoints, and request help from any iOS or Android device." },
];

const PAIN_POINTS = [
  { title: "Fragmented tools", desc: "One app for scheduling, another for dispatch, spreadsheets for hours, and email for incidents. Data lives in silos and supervisors waste time reconciling it." },
  { title: "No real-time visibility", desc: "You don't know a guard missed a checkpoint or a site is uncovered until the client calls — or worse, an incident escalates." },
  { title: "Manual client reporting", desc: "Building end-of-shift or monthly reports by hand steals hours every week, and clients still question whether they got what they paid for." },
];

const HOW_IT_WORKS = [
  { step: "1", title: "Set up sites & schedules", desc: "Add sites, post orders, and publish recurring schedules in minutes. Officers get mobile notifications for every shift." },
  { step: "2", title: "Run operations from the field", desc: "Guards clock in, scan checkpoints, file incidents, and stay in touch with dispatch — all from one mobile app." },
  { step: "3", title: "Prove service & bill accurately", desc: "Coverage, incidents, and activity data flow into custom reports and client-ready PDFs that justify your invoice." },
];

const AUDIENCES = [
  { title: "Private security firms", desc: "From solo contractors to 200-guard operations, replace the patchwork of apps with a single operations center." },
  { title: "Campus security", desc: "Manage patrol routes, parking enforcement, and incident documentation across multiple buildings and shifts." },
  { title: "Patrol services", desc: "Verify tour completion, track vehicles, and deliver branded proof-of-service reports to clients." },
  { title: "Private investigators", desc: "Log case activity, attach evidence, and keep reports organized with timestamped location data." },
];

const QUICK_WINS = [
  { icon: Clock, label: "Clock in/out from the phone" },
  { icon: MapPin, label: "Prove coverage with GPS" },
  { icon: FileText, label: "Auto-build client reports" },
  { icon: Shield, label: "Reduce incident response time" },
  { icon: Bell, label: "Real-time alerts" },
  { icon: QrCode, label: "QR checkpoint verification" },
];

function SecurityGuardManagementPage() {
  return (
    <MarketingShell>
      <MarketingHero
        eyebrow="Security Guard Management Software"
        title={<>Security guard management software for <span className="text-primary">modern field operations</span></>}
        subtitle="Scheduling, dispatch, GPS tracking, incident reporting, patrol verification, and client-ready reports — built for security firms, campus teams, and patrol services."
      />

      {/* Problem / Pain Points */}
      <section className="border-b border-border/60 bg-gradient-to-b from-transparent to-primary/[0.03] py-16 md:py-20">
        <PageContainer>
          <div className="text-center">
            <h2 className="font-display text-3xl font-bold md:text-4xl">The cost of running security on disconnected tools</h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
              Most security organizations stitch together spreadsheets, radios, group chats, and paper logs. The result is delayed response, billing disputes, and clients who aren't sure what they paid for.
            </p>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-3">
            {PAIN_POINTS.map((p) => (
              <div key={p.title} className="rounded-2xl border border-border bg-card p-6">
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
                  <Shield className="h-5 w-5" />
                </div>
                <h3 className="font-display text-lg font-semibold">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
              </div>
            ))}
          </div>
        </PageContainer>
      </section>

      <FeatureGrid items={FEATURES} heading="Everything included" />

      {/* How it works */}
      <section className="border-t border-border/60 bg-gradient-to-b from-transparent to-primary/[0.03] py-16 md:py-20">
        <PageContainer>
          <div className="text-center">
            <h2 className="font-display text-3xl font-bold md:text-4xl">How it works</h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">Replace the tool stack in three steps — no IT department required.</p>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-3">
            {HOW_IT_WORKS.map((h) => (
              <div key={h.step} className="relative rounded-2xl border border-border bg-card p-6">
                <span className="absolute -top-3 left-6 inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                  {h.step}
                </span>
                <h3 className="mt-3 font-display text-lg font-semibold">{h.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{h.desc}</p>
              </div>
            ))}
          </div>
        </PageContainer>
      </section>

      {/* Who it's for */}
      <section className="border-t border-border/60 py-16 md:py-20">
        <PageContainer>
          <div className="text-center">
            <h2 className="font-display text-3xl font-bold md:text-4xl">Built for every kind of security operation</h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">Whether you run a 5-person patrol route or a 200-officer firm, MissionReady scales with you.</p>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {AUDIENCES.map((a) => (
              <div key={a.title} className="rounded-2xl border border-border bg-card p-6">
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Users className="h-5 w-5" />
                </div>
                <h3 className="font-display text-lg font-semibold">{a.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{a.desc}</p>
              </div>
            ))}
          </div>
        </PageContainer>
      </section>

      {/* Trust / Why MissionReady */}
      <section className="border-t border-border/60 py-16 md:py-20">
        <PageContainer>
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 className="font-display text-3xl font-bold md:text-4xl">One platform,<br />not ten subscriptions</h2>
              <p className="mt-4 text-muted-foreground">
                MissionReady replaces the scattered stack of scheduling, dispatch, tracking, reporting, and billing tools. Supervisors get real-time visibility, officers get a simple mobile app, and clients get proof that work happened.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-4">
                {QUICK_WINS.map((w) => (
                  <div key={w.label} className="flex items-center gap-2 text-sm">
                    <w.icon className="h-4 w-4 text-primary" />
                    <span>{w.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-card p-8">
              <h3 className="font-display text-lg font-semibold">What it looks like on a shift</h3>
              <ul className="mt-5 space-y-5 text-sm">
                <li className="flex gap-3">
                  <FileText className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <div>
                    <p className="font-semibold">Reporting</p>
                    <p className="text-muted-foreground">An officer speaks an observation into Voice D.A.R. It's transcribed, summarized, and added to the daily activity report with a timestamp — and the original recording stays available for playback.</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <Calendar className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <div>
                    <p className="font-semibold">Scheduling</p>
                    <p className="text-muted-foreground">A supervisor builds shifts by site, sees open coverage gaps, and officers clock in and out from their phones. Unscheduled clock-ins are flagged for review.</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <QrCode className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <div>
                    <p className="font-semibold">Patrols</p>
                    <p className="text-muted-foreground">Officers scan QR checkpoints along a guard tour. Each scan is logged with the time, so supervisors can see which checkpoints were completed.</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </PageContainer>
      </section>

      {/* Deep feature links */}
      <section className="border-t border-border/60 bg-gradient-to-b from-transparent to-primary/[0.03] py-16 md:py-20">
        <PageContainer>
          <div className="text-center">
            <h2 className="font-display text-3xl font-bold md:text-4xl">Explore the modules</h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">Each part of MissionReady is also a standalone capability. Dive deeper into the features that matter most to your operation.</p>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <Link to="/voice-dar" className="group rounded-2xl border border-border bg-card p-6 transition-colors hover:border-primary/50">
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary">
                <Smartphone className="h-5 w-5" />
              </div>
              <h3 className="font-display text-lg font-semibold group-hover:text-primary">Voice D.A.R.™ reporting</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Hands free, eyes stay observing. Officers speak their daily activity report and MissionReady transcribes it.</p>
            </Link>
            <Link to="/dispatch" className="group rounded-2xl border border-border bg-card p-6 transition-colors hover:border-primary/50">
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary">
                <Radio className="h-5 w-5" />
              </div>
              <h3 className="font-display text-lg font-semibold group-hover:text-primary">Dispatch</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Assign tasks, broadcast alerts, and keep field teams in sync with real-time communication.</p>
            </Link>
            <Link to="/patrol" className="group rounded-2xl border border-border bg-card p-6 transition-colors hover:border-primary/50">
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary">
                <MapPin className="h-5 w-5" />
              </div>
              <h3 className="font-display text-lg font-semibold group-hover:text-primary">Patrol & QR tours</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Verify patrol completion with QR checkpoints and replay tours on a GPS map.</p>
            </Link>
            <Link to="/incident-reporting" className="group rounded-2xl border border-border bg-card p-6 transition-colors hover:border-primary/50">
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary">
                <FileText className="h-5 w-5" />
              </div>
              <h3 className="font-display text-lg font-semibold group-hover:text-primary">Incident reporting</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Capture incidents from mobile with photo evidence, GPS stamps, and auto-escalation.</p>
            </Link>
            <Link to="/invoicing-software" className="group rounded-2xl border border-border bg-card p-6 transition-colors hover:border-primary/50">
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary">
                <LayoutDashboard className="h-5 w-5" />
              </div>
              <h3 className="font-display text-lg font-semibold group-hover:text-primary">Invoicing</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Turn verified shift and patrol data into accurate invoices without manual entry.</p>
            </Link>
            <Link to="/custom-reports" className="group rounded-2xl border border-border bg-card p-6 transition-colors hover:border-primary/50">
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary">
                <Calendar className="h-5 w-5" />
              </div>
              <h3 className="font-display text-lg font-semibold group-hover:text-primary">Custom reports</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Build no-code report templates and export branded PDFs for any client or stakeholder.</p>
            </Link>
            <Link to="/contact" className="group rounded-2xl border border-border bg-card p-6 transition-colors hover:border-primary/50">
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary">
                <MessageSquare className="h-5 w-5" />
              </div>
              <h3 className="font-display text-lg font-semibold group-hover:text-primary">Talk to us</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Not sure which modules fit your operation? Ask about pricing, migration, or a live demo.</p>
            </Link>
          </div>
        </PageContainer>
      </section>

      {/* Comparison */}
      <section className="border-t border-border/60 py-16 md:py-20">
        <PageContainer>
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-display text-3xl font-bold md:text-4xl">How MissionReady compares</h2>
            <p className="mt-3 text-muted-foreground">
              Side-by-side with the security guard management platforms operators evaluate most: THERMS, TrackTik, and Silvertrac.
            </p>
          </div>

          {/* Mobile */}
          <div className="mt-10 space-y-3 md:hidden">
            {COMPARE_ROWS.map((row) => (
              <div key={row.feature} className="rounded-xl border border-border bg-card p-4">
                <div className="text-sm font-medium">{row.feature}</div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <div className="flex items-center justify-between rounded-lg bg-background/60 px-3 py-2">
                    <span className="text-xs font-semibold text-primary">MissionReady</span>
                    <CompareIcon value={row.mr} />
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-background/60 px-3 py-2">
                    <span className="text-xs text-muted-foreground">THERMS</span>
                    <CompareIcon value={row.therms} />
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-background/60 px-3 py-2">
                    <span className="text-xs text-muted-foreground">TrackTik</span>
                    <CompareIcon value={row.tracktik} />
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-background/60 px-3 py-2">
                    <span className="text-xs text-muted-foreground">Silvertrac</span>
                    <CompareIcon value={row.silvertrac} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop */}
          <div className="mt-10 hidden overflow-x-auto rounded-2xl border border-border bg-card md:block">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="border-b border-border bg-muted/30">
                <tr>
                  <th className="px-5 py-4 font-semibold">Capability</th>
                  <th className="px-5 py-4 text-center font-semibold text-primary">MissionReady</th>
                  <th className="px-5 py-4 text-center font-semibold">THERMS</th>
                  <th className="px-5 py-4 text-center font-semibold">TrackTik</th>
                  <th className="px-5 py-4 text-center font-semibold">Silvertrac</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((row, i) => (
                  <tr key={row.feature} className={i % 2 === 0 ? "bg-background/40" : ""}>
                    <td className="px-5 py-3 font-medium">{row.feature}</td>
                    <td className="px-5 py-3 text-center"><CompareIcon value={row.mr} /></td>
                    <td className="px-5 py-3 text-center"><CompareIcon value={row.therms} /></td>
                    <td className="px-5 py-3 text-center"><CompareIcon value={row.tracktik} /></td>
                    <td className="px-5 py-3 text-center"><CompareIcon value={row.silvertrac} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> Included</span>
            <span className="inline-flex items-center gap-2"><Minus className="h-4 w-4 text-amber-500" /> Partial / add-on</span>
            <span className="inline-flex items-center gap-2"><X className="h-4 w-4 text-muted-foreground/60" /> Not available</span>
          </div>

          <div className="mt-8 flex justify-center">
            <Button asChild variant="outline"><Link to="/compare">See the full comparison →</Link></Button>
          </div>
        </PageContainer>
      </section>

      {/* FAQ */}
      <section className="border-t border-border/60 py-16 md:py-20">
        <PageContainer>
          <div className="text-center">
            <h2 className="font-display text-3xl font-bold md:text-4xl">Frequently asked questions</h2>
          </div>
          <div className="mx-auto mt-12 max-w-3xl space-y-4">
            {FAQ_ITEMS.map((faq) => (
              <details key={faq.q} className="group rounded-xl border border-border bg-card px-6 py-4 open:border-primary/50">
                <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold md:text-base">
                  {faq.q}
                  <span className="ml-4 text-primary transition-transform group-open:rotate-180">▼</span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{faq.a}</p>
              </details>
            ))}
          </div>
        </PageContainer>
      </section>

      <CtaBand title="Stop stitching tools together" subtitle="Start your free 30-day trial. No credit card required. Set up your first site and officers in under 15 minutes." />
    </MarketingShell>
  );
}

const FAQ_ITEMS = [
  {
    q: "What is security guard management software?",
    a: "Security guard management software is an all-in-one operations platform that helps security companies schedule officers, dispatch tasks, track GPS locations, verify patrols, file incident reports, and deliver client-ready proof of service — replacing the mix of spreadsheets, radios, and paper logs.",
  },
  {
    q: "Who is MissionReady built for?",
    a: "MissionReady is built for private security firms, campus security teams, patrol services, private investigators, and any organization that needs to manage physical security personnel in the field. It scales from a solo 1099 contractor up to multi-site operations with hundreds of officers.",
  },
  {
    q: "Do officers need a special device?",
    a: "No. Officers use the MissionReady web app on any modern smartphone or tablet. There's nothing to install from an app store — the app works as a progressive web app (PWA) and supports offline data capture for remote sites with poor connectivity.",
  },
  {
    q: "Can clients see what work was done?",
    a: "Yes. The client portal gives authorized contacts a live view of coverage, incidents, and patrol completion. You can also export branded PDF reports with your logo and colors for monthly reviews, insurance claims, or billing justification.",
  },
  {
    q: "How is this different from TrackTik or Silvertrac?",
    a: "MissionReady combines the core capabilities of legacy guard management platforms — scheduling, GPS tracking, dispatch, and incident reporting — with transparent pricing, no mandatory sales calls, a free 30-day trial, and modern features like AI-assisted report writing and custom no-code report templates.",
  },
  {
    q: "How quickly can we get started?",
    a: "Most teams set up their first site, add officers, and publish a schedule within 15 minutes. If you are migrating from another platform, you can import sites, officers, and clients via CSV to speed up the transition.",
  },
];
